import { Loader2, Plus, User } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MetricsTotalizers } from './components/MetricsTotalizers';
import { OnBoardingTable } from './components/OnBoardingTable';

import { NoContentFallback } from '@/components/NoContentFallBack';
import { Pagination } from '@/components/Pagination';
import { SearchCustomersForm } from '@/components/SearchCustomers';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ROUTE_PATHS } from '@/config/constants';
import { useGetPaginatedCustomers } from '@/hooks/useGetPaginatedCustomers';
import { Platform, Status } from '@/services/CustomerService';
interface Filters {
  searchTerm: string | null;
  platform: Platform | null;
  status: Status | null;
}

export function AdminOnBoarding() {
  const navigate = useNavigate();
  const [search, setSearch] = useState<Partial<Filters>>({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const handleSearchChange = (partial: Partial<Filters>) => {
    setSearch((prev) => ({
      ...prev,
      ...partial,
    }));
    setPage(1);
  };

  const { customers, totalPages, isLoading } = useGetPaginatedCustomers({
    page: page,
    limit: perPage,
    status: Status.ACTIVE,
    ...(search.searchTerm != null && { searchTerm: search.searchTerm }),
    ...(search.platform != null && { platform: search.platform }),
  });

  return (
    <Card className="min-h-screen rounded-lg shadow">
      <CardContent>
        <div className="mt-2 border-b bg-white">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Onboarding Dashboard
                </h1>
                <p className="mt-1 text-gray-600">
                  View and manage customer metrics
                </p>
              </div>
              <Button
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => navigate(ROUTE_PATHS.ADMIN_CUSTOMERS_CREATE)}
              >
                <Plus className="mr-2 h-4 w-4" />
                New Client
              </Button>
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-gray-50">
          <div className="container mx-auto space-y-6 px-4 py-6">
            <MetricsTotalizers />

            <div className="flex flex-row justify-end gap-4">
              <div className="w-1/8 hidden">
                <Select
                  value={perPage.toString()}
                  onValueChange={(value) => {
                    setPerPage(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Items per page" />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 20, 50].map((n) => (
                      <SelectItem key={n} value={n.toString()}>
                        {n} per page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <SearchCustomersForm
                onPageChange={setPage}
                onSearchChange={handleSearchChange}
              />
            </div>

            {customers.length > 0 && !isLoading && (
              <>
                <OnBoardingTable customers={customers} perPage={perPage} />
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  isLoading={isLoading}
                />
              </>
            )}

            {isLoading && (
              <div className="flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}

            {!customers.length && !isLoading && (
              <NoContentFallback
                description="You have not onboarded any customers with this name or ID yet."
                title="No customers found"
                icon={User}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
